import type { PetMissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation/';
import type {
    CatDTO,
    DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet/';
import { TextField, CheckboxField, Box, GridItem, Grid } from '@flos/react-ui';
import { useRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import {
    isMissingPetChipId,
    isMissingPetPoliceDogId
} from '../configs/CheckMissingCoverableConfig';
import { useHippoText } from '../../../hooks/useHippoText';
import { FieldBox } from './missingInformationCoverables/FieldBox';

const PetMissingInformationForm = ({
    petMissingDTO,
    coverableDTO
}: {
    petMissingDTO: PetMissingInformationDTO;
    coverableDTO: CatDTO | DogDTO;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [missingInformation, setMissingInformation] = useRecoilState(missingInformationAtom);
    const hasMissingChipId = isMissingPetChipId(coverableDTO);
    const hasMissingPetPoliceDogId = isMissingPetPoliceDogId(coverableDTO);

    if (!hasMissingChipId && !hasMissingPetPoliceDogId) return <></>;
    const { fixedId, chipNumber, hasNoChipNumber, policeDogId } = petMissingDTO;

    if (!fixedId) return <></>;

    const updateMissingInformationProperty = (
        fixedId: string,
        updateValue: Record<string, any>
    ): void => {
        const updatedPet = missingInformation.pet?.map((item) => {
            if (item.fixedId !== fixedId) return item;
            return {
                ...item,
                ...updateValue
            };
        });
        setMissingInformation((previousState) => {
            return {
                ...previousState,
                pet: updatedPet
            };
        });
    };

    return (
        <Box>
            <Grid gap={4}>
                <GridItem span={12}>
                    <h6>{coverableDTO.flavourName}</h6>
                </GridItem>
                <FieldBox label={coverableDTO.name}>
                    {hasMissingPetPoliceDogId && (
                        <TextField
                            id={`missing-information-police-dog-id-${fixedId}`}
                            maxLength={255}
                            label={getHippoText('DSS-PET-OPC-CO-POLICE-ID-INPUT-TOOLTIP')}
                            defaultValue={policeDogId ?? ''}
                            onBlur={(data) => {
                                updateMissingInformationProperty(fixedId ?? '', {
                                    policeDogId: data.currentTarget.value
                                });
                            }}
                            showLength={false}
                        />
                    )}
                    {hasMissingChipId && (
                        <>
                            <TextField
                                wrapperClassName="u-spacing-stack-bottom-5"
                                id={`missing-information-pet-chip-id-${fixedId}`}
                                maxLength={255}
                                label={getHippoText('DSS-PET-ONSA-EDIT-CHIP-NUMBER-INPUT-TOOLTIP')}
                                disabled={hasNoChipNumber}
                                value={chipNumber ?? ''}
                                onChange={(event) => {
                                    updateMissingInformationProperty(fixedId ?? '', {
                                        chipNumber: event.target.value,
                                        hasNoChipNumber: false
                                    });
                                }}
                                showLength={false}
                            />
                            <CheckboxField
                                id={`missing-information-pet-has-chip-id-${fixedId}`}
                                label={getHippoText('DSS-PET-ONSA-CO-MISSING-CHIP-MESSAGE')}
                                checked={hasNoChipNumber}
                                onChange={(event) => {
                                    updateMissingInformationProperty(fixedId ?? '', {
                                        hasNoChipNumber: event.target.checked,
                                        chipNumber: ''
                                    });
                                }}
                            />
                        </>
                    )}
                </FieldBox>
            </Grid>
        </Box>
    );
};
export default PetMissingInformationForm;
